/**
 * @fileoverview
 * Constants and helpers used by the Grid component.
 */

import { WidgetSizeEnum } from '@core/types'

export enum ScrollDirectionEnum {
  PREVIOUS = 'scroll-previous',
  NEXT = 'scroll-next',
}

export const RESPONSIVE_THUMBNAIL_WIDTHS = {
  [WidgetSizeEnum.SMALL]: {
    minThumbWidth: 255,
    maxThumbWidth: 255,
    maxColumns: 5,
  },
  [WidgetSizeEnum.MEDIUM]: {
    minThumbWidth: 255,
    maxThumbWidth: 275,
    maxColumns: 5,
  },
  [WidgetSizeEnum.LARGE]: {
    minThumbWidth: 255,
    maxThumbWidth: 300,
    maxColumns: 4,
  },
}

export const DEFAULT_THUMBNAIL_GAP_PX = 10
/**
 *
 * number of responsive columns is the number of max width thumbs
 * that can fit in the carousel viewport width, rounding up.
 * This lets the thumbnails shrink for a bit before breaking down to
 * the next column.
 *
 * We subtract one gap from the width because gaps
 * are between columns so there's one less gap than columns
 *
 * @param viewportWidth
 * @returns
 */
export function responsiveColumnsForWidth({
  viewportWidth,
  maxThumbWidth,
  gap,
  maxColumns,
}: {
  viewportWidth: number
  maxThumbWidth: number
  gap: number
  maxColumns: number
}): number {
  const curVisibleCols = Math.ceil(
    (viewportWidth - gap) / (maxThumbWidth + gap),
  )

  return Math.min(maxColumns, curVisibleCols)
}

/**
 * Calculate column count for grid mode where all columns must fit
 * without overflow. Picks the column count that keeps each cell
 * between minThumbWidth and maxThumbWidth. When that's geometrically
 * impossible, prefers fewer columns (slightly above max) over more
 * columns (below min).
 */
export function gridColumnsForWidth({
  viewportWidth,
  minThumbWidth,
  maxThumbWidth,
  gap,
}: {
  viewportWidth: number
  minThumbWidth: number
  maxThumbWidth: number
  gap: number
}): number {
  // Minimum columns so cells don't exceed maxThumbWidth
  const minCols = Math.ceil((viewportWidth + gap) / (maxThumbWidth + gap))
  // Maximum columns while keeping cells at least minThumbWidth
  const maxCols = Math.floor((viewportWidth + gap) / (minThumbWidth + gap))

  return Math.max(1, Math.min(minCols, maxCols))
}
/**
 * Calculate the number of columns for a given width
 * for a embed-feed's "grid" mode.
 */
export const columnsForWidth = (width: number): number => {
  if (width >= 960) {
    return 5
  } else if (width >= 640) {
    return 4
  } else if (width >= 480) {
    return 3
  }
  return 2
}

const SM_MIN_NEW_CARD_WIDTH_BELOW_1024 = 346
const SM_MIN_NEW_CARD_WIDTH_ABOVE_1024 = 346
const MD_MIN_NEW_CARD_WIDTH_BELOW_1024 = 338
const MD_MIN_NEW_CARD_WIDTH_ABOVE_1024 = 350
const LG_MIN_NEW_CARD_WIDTH_BELOW_1024 = 480
const LG_MIN_NEW_CARD_WIDTH_ABOVE_1024 = 492

type ColumnsByVideoCountArgs = {
  containerWidth: number
  count: number
  size?: WidgetSizeEnum
}

const getDynamicCarouselMinWidths = (size?: WidgetSizeEnum) => {
  switch (size) {
    case WidgetSizeEnum.MEDIUM:
      return {
        minWidthBelow1024: MD_MIN_NEW_CARD_WIDTH_BELOW_1024,
        minWidthAbove1024: MD_MIN_NEW_CARD_WIDTH_ABOVE_1024,
      }
    case WidgetSizeEnum.SMALL:
      return {
        minWidthBelow1024: SM_MIN_NEW_CARD_WIDTH_BELOW_1024,
        minWidthAbove1024: SM_MIN_NEW_CARD_WIDTH_ABOVE_1024,
      }
    default:
      return {
        minWidthBelow1024: LG_MIN_NEW_CARD_WIDTH_BELOW_1024,
        minWidthAbove1024: LG_MIN_NEW_CARD_WIDTH_ABOVE_1024,
      }
  }
}

/**
 * [CS-6201] Calculate number of columns based on number of videos for medium carousel V2
 * and decide if we're showing the new card style or not
 */
export const columnsByVideoCount = ({
  count,
  containerWidth,
  size,
}: ColumnsByVideoCountArgs) => {
  const { minWidthAbove1024, minWidthBelow1024 } =
    getDynamicCarouselMinWidths(size)

  const cardWidth =
    containerWidth < 1024 ? minWidthBelow1024 : minWidthAbove1024

  // only show if the container is wide enough to show all cards
  const showV2Card = (containerWidth - (count - 1) * 10) / count >= cardWidth

  const columns = showV2Card ? count : columnsForWidth(containerWidth)

  return { columns, showV2Card }
}
